/**
 * <WalkInDrawer> — canonical walk-in composer (single scrollable form, 5 collapsible sections).
 * Follows D2: Walk-In → Reservation → Stay → Folio → Check-In (+ optional advance payment → receipt).
 * Uses <FloorRoomPicker> (D7) — no dropdowns. Rate field is the shared <RateInputWithTax>.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { OperationalDrawer, OperationalButton, FloorRoomPicker } from "@/components/operational";
import { UserPlus, User, BedDouble, IndianRupee, FileText, CreditCard, Check } from "lucide-react";
import { foApi, formatApiError } from "../foApi";
import api from "@/lib/api";
import DuplicateGuestPanel, { recordCreateNewDecision } from "@/components/duplicate/DuplicateGuestPanel";
import GovtIdUploader from "@/components/GovtIdUploader";
import { DatePicker } from "@/components/ui/date-picker";
import { CollapsibleSection } from "@/components/form/CollapsibleSection";
import { RateInputWithTax, nightsBetween } from "@/components/pms/RateInput";
import { ReservationSourceInput } from "@/components/pms/ReservationSourceInput";
import { MultiRoomSelector, emptyRoomLine } from "@/components/pms/MultiRoomSelector";
import { docsAPI, fmtINR } from "@/lib/documents";
import { notifyDocument } from "@/components/documents/documentToast";
import { useIdTypes } from "@/hooks/useIdTypes";
import { NumberInput, blankZero } from "@/components/ui/number-input";

const PAY_METHODS = [["cash", "Cash"], ["card", "Card"], ["upi", "UPI"], ["ota", "OTA"]];
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(String(d).slice(0, 10)); x.setDate(x.getDate() + n); return iso(x); };

const initialForm = () => ({
  salutation: "Mr", first_name: "", last_name: "", primary_phone: "", email: "",
  govt_id_type: "aadhaar", govt_id_number: "", govt_id_document_url: "", nationality: "",
  expected_arrival: iso(new Date()), expected_departure: iso(new Date(Date.now() + 86400000)),
  arrival_time: "", departure_time: "", num_adults: 1, num_children: 0,
  room: null, entered_rate: "", tax_inclusive_input: false, rate_plan_id: "", breakfast_included: false,
  special_requests: "", early_checkin: false, late_checkout: false,
  reservation_source: "Direct / Walk-In", reservation_source_ref: "",
  pay_method: "cash", pay_reference: "", advance_amount: "",
});

export default function WalkInDrawer({ open, onOpenChange, hotelId, onSuccess, prefill = null }) {
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [taxConfigured, setTaxConfigured] = useState(true);
  const [breakdown, setBreakdown] = useState(null);
  const [roomLines, setRoomLines] = useState([emptyRoomLine()]);
  const [groupSummary, setGroupSummary] = useState(null);
  const multi = roomLines.length > 1;
  const changeLines = (ls) => { setRoomLines(ls); if (ls.length === 1 && ls[0].room_id) { const r = rooms.find(x => x.id === ls[0].room_id); setForm(f => ({ ...f, room: r || f.room, entered_rate: ls[0].entered_rate, tax_inclusive_input: !!ls[0].tax_inclusive_input, rate_plan_id: ls[0].rate_plan_id || "" })); } };
  const [dupResolution, setDupResolution] = useState({ matches: [], reason: null, allowProceed: false, existingGuestId: null });

  useEffect(() => {
    if (!open) return;
    setForm(initialForm());
    setRoomLines([emptyRoomLine()]); setGroupSummary(null);
    setDupResolution({ matches: [], reason: null, allowProceed: false, existingGuestId: null });
    setLoadingRooms(true);
    // Show every room; the picker's per-card `assignable` flag (backend snapshot logic) decides selectability.
    foApi.rooms(hotelId ? { hotel_id: hotelId } : {})
      .then(r => {
        const items = r.items || [];
        setRooms(items);
        // ROOM-OPS-1 · launched from a room card → room + rate pre-filled, FDO never re-selects
        if (prefill?.room_id) {
          const room = items.find(x => x.id === prefill.room_id);
          const rate = prefill.entered_rate ?? "";
          const dates = prefill.expected_arrival ? { expected_arrival: prefill.expected_arrival, expected_departure: prefill.expected_departure || addDays(prefill.expected_arrival, 1) } : {};
          if (room) setForm(f => ({ ...f, room, entered_rate: rate, ...dates })); else if (prefill.expected_arrival) setForm(f => ({ ...f, ...dates }));
          setRoomLines([{ ...emptyRoomLine(), room_id: prefill.room_id, entered_rate: rate }]);
        }
      }).catch(() => setRooms([])).finally(() => setLoadingRooms(false));
  }, [open, hotelId, prefill?.room_id, prefill?.entered_rate]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => setForm(f => ({ ...f, ...patch }));
  const nights = nightsBetween(form.expected_arrival, form.expected_departure);
  const totalPayable = multi ? (groupSummary?.grand || 0) : (breakdown?.grand || 0);
  const roomOptions = useMemo(() => rooms.filter(r => r.assignable).map(r => ({ id: r.id, room_type_id: r.room_type_id, label: `${r.number}${r.type_label ? ` — ${r.type_label}` : ""}` })), [rooms]);
  const advance = Number(form.advance_amount) || 0;

  const blocker = !form.first_name ? "Guest first name is required" : !form.primary_phone ? "Guest phone is required"
    : (multi ? roomLines.some(l => !l.room_id) : !form.room) ? "Select a room" : nights < 1 ? "Departure must be after arrival"
    : !taxConfigured ? "Tax rates not configured for this hotel"
    : (multi ? roomLines.some(l => !(Number(l.entered_rate) > 0)) : !(Number(form.entered_rate) > 0)) ? "Enter a nightly rate for every room" : null;

  const submit = async () => {
    if (blocker) { toast.error(blocker); return; }
    setSaving(true);
    try {
      const res = await foApi.walkIn({
        hotel_id: hotelId, guest_id: dupResolution.existingGuestId || undefined,
        salutation: form.salutation, first_name: form.first_name, last_name: form.last_name,
        primary_phone: form.primary_phone, email: form.email || null,
        govt_id_type: form.govt_id_type, govt_id_number: form.govt_id_number || null,
        govt_id_document_url: form.govt_id_document_url || null, nationality: form.nationality || null,
        expected_arrival: form.expected_arrival, expected_departure: form.expected_departure,
        expected_arrival_time: form.arrival_time || null, expected_departure_time: form.departure_time || null,
        num_adults: form.num_adults, num_children: form.num_children,
        special_requests: form.special_requests || null,
        early_checkin_requested: form.early_checkin, late_checkout_requested: form.late_checkout,
        reservation_source: form.reservation_source?.trim() || null, reservation_source_ref: form.reservation_source_ref?.trim() || null,
        room_id: multi ? roomLines[0].room_id : form.room?.id,
        rooms: multi ? roomLines.map(l => ({ room_id: l.room_id, pax_adults: Number(l.pax_adults) || 1, pax_children: Number(l.pax_children) || 0, entered_rate: Number(l.entered_rate), tax_inclusive_input: !!l.tax_inclusive_input, rate_plan_id: l.rate_plan_id || null, extra_bed: !!l.extra_bed, notes: l.notes || null })) : undefined,
        entered_rate: multi ? Number(roomLines[0].entered_rate) : Number(form.entered_rate), tax_inclusive_input: multi ? !!roomLines[0].tax_inclusive_input : !!form.tax_inclusive_input,
        rate_plan_id: form.rate_plan_id || null,
        breakfast_included: !!form.breakfast_included,
        possible_duplicate_of: dupResolution.matches.length ? dupResolution.matches.map(m => m.id) : undefined,
        duplicate_reason: dupResolution.reason || undefined,
      });
      if (dupResolution.matches.length && dupResolution.reason && res?.guest_id && !dupResolution.existingGuestId) {
        await recordCreateNewDecision({ source: "walk_in", matches: dupResolution.matches, newGuestId: res.guest_id, reason: dupResolution.reason, payloadSnapshot: form });
      }
      if (advance > 0 && res?.folio_id) {
        try {
          const { data: pay } = await api.post("/pms/payments/", { folio_id: res.folio_id, method: form.pay_method, amount: advance, reference: form.pay_reference || null });
          const rec = await docsAPI.createReceipt({ payment_id: pay.id, receipt_type: "room" });
          notifyDocument(`Walk-in checked in · ${fmtINR(advance)} received · Receipt ${rec.receipt_number}`, { url: docsAPI.receiptPdfUrl(rec.id), filename: `Receipt-${rec.receipt_number}.pdf`, testId: "walkin-receipt-toast" });
        } catch (e) {
          toast.error(`Checked in, but payment/receipt failed: ${formatApiError(e, "payment error")}`);
        }
      } else {
        toast.success(form.breakfast_included ? "Walk-in checked in — breakfast added to F&B register" : "Walk-in checked in");
      }
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e, "Walk-in failed"));
    } finally { setSaving(false); }
  };

  const onDupDecision = (decision, matches, extras = {}) => {
    if (decision === "continue_with_existing") {
      setDupResolution({ matches, reason: null, allowProceed: true, existingGuestId: extras.existingGuestId });
      toast.success("Existing guest will be used for this walk-in");
    } else if (decision === "create_new_with_reason") {
      setDupResolution({ matches, reason: extras.reason, allowProceed: true, existingGuestId: null });
      toast.info("Confirmed — a new guest will be created despite matches");
    } else if (decision === "ignore") {
      setDupResolution({ matches: [], reason: null, allowProceed: true, existingGuestId: null });
    }
  };

  const sec = (id, title, icon, summary) => ({ id, title, icon, summary, tone: "light", storageKey: "walkin", testid: `walkin-section-${id}` });

  return (
    <OperationalDrawer open={open} onOpenChange={onOpenChange} title="New Walk-In"
      subtitle="Guest → Reservation → Stay → Folio → Check-In" size="lg" testid="walkin-drawer"
      footer={
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="walkin-footer">
          <div data-testid="walkin-total-payable">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total Payable</div>
            <div className="text-[20px] font-bold mono text-slate-900 leading-tight" data-testid="walkin-total-payable-amount">{fmtINR(totalPayable)}</div>
            <div className="text-[11px] text-slate-500">{breakdown?.nights ?? nights ?? 0} night{(breakdown?.nights ?? nights) === 1 ? "" : "s"} · incl. GST{advance > 0 ? ` · advance ${fmtINR(advance)}` : ""}</div>
          </div>
          <div className="flex gap-2">
            <OperationalButton label="Cancel" tone="secondary" size="secondary" onClick={() => onOpenChange(false)} testid="walkin-cancel" />
            <OperationalButton label={saving ? "Creating..." : advance > 0 ? "Check In & Print Receipt" : "Check In"}
              icon={UserPlus} tone="success" size="primary" onClick={submit} loading={saving} disabled={!!blocker} testid="walkin-confirm" />
          </div>
        </div>
      }>
      <div className="p-4 space-y-3" data-testid="walkin-form" data-hocp-form="">
        <CollapsibleSection {...sec("guest", "Guest Details", User, [form.salutation, form.first_name, form.last_name].filter(Boolean).join(" ") || "—")}>
          <GuestSection form={form} update={update} />
          <DuplicateGuestPanel
            payload={{ first_name: form.first_name, last_name: form.last_name, primary_phone: form.primary_phone, email: form.email, govt_id_number: form.govt_id_number }}
            source="walk_in" allowContinueWithExisting={true} onDecision={onDupDecision} testid="walkin-dup-panel" />
        </CollapsibleSection>

        <CollapsibleSection {...sec("stay", "Stay Details", BedDouble, `${form.expected_arrival} → ${form.expected_departure} · ${form.room ? `Room ${form.room.number}` : "no room"}`)}>
          <StaySection form={form} update={update} nights={nights} rooms={rooms} loadingRooms={loadingRooms} multi={multi}
            roomCount={<div className="flex items-center gap-1" data-testid="walkin-room-count">{[1, 2, 3, 4, 5].map(n => <button key={n} type="button" onClick={() => { if (n === 1) { changeLines([roomLines[0] || emptyRoomLine()]); return; } const first = multi ? roomLines[0] : { ...emptyRoomLine(), room_id: form.room?.id || "", entered_rate: form.entered_rate, tax_inclusive_input: form.tax_inclusive_input, rate_plan_id: form.rate_plan_id, pax_adults: form.num_adults, pax_children: form.num_children }; const rest = roomLines.slice(1, n); setRoomLines([first, ...rest, ...Array.from({ length: Math.max(0, n - 1 - rest.length) }, emptyRoomLine)]); }}
              className={`h-10 min-w-[40px] px-2 rounded-lg border-2 text-[13px] font-medium ${roomLines.length === n || (n === 5 && roomLines.length > 5) ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white border-slate-300"}`} data-testid={`walkin-room-count-${n}`}>{n}{n === 5 ? "+" : ""}</button>)}</div>}
            multiRoom={multi && <MultiRoomSelector tone="light" value={roomLines} onChange={changeLines} roomOptions={roomOptions} nights={nights} testidPrefix="walkin-mr" onSummary={setGroupSummary} onConfigured={setTaxConfigured} hideCount />} />
        </CollapsibleSection>

        {!multi && <CollapsibleSection {...sec("rate", "Rate & Charges", IndianRupee, breakdown ? `${fmtINR(breakdown.totalNight)}/night · ${fmtINR(breakdown.grand)} total` : "no rate")}>
          <RateInputWithTax tone="light" testidPrefix="walkin-rate" nights={nights} roomTypeId={form.room?.room_type_id}
            value={{ entered_rate: form.entered_rate, tax_inclusive_input: form.tax_inclusive_input, rate_plan_id: form.rate_plan_id }}
            onChange={(v) => update(v)} onConfigured={setTaxConfigured} onBreakdown={setBreakdown} />
        </CollapsibleSection>}

        <CollapsibleSection {...sec("requests", "Special Requests", FileText, form.special_requests || "—")}>
          <RequestsSection form={form} update={update} />
        </CollapsibleSection>

        <CollapsibleSection {...sec("payment", "Payment", CreditCard, advance > 0 ? `${fmtINR(advance)} · ${form.pay_method}` : "no advance")}>
          <PaymentSection form={form} update={update} totalPayable={totalPayable} advance={advance} />
        </CollapsibleSection>
      </div>
    </OperationalDrawer>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function TextInput(props) {
  const shown = props.type === "number" && "value" in props ? { value: blankZero(props.value) ?? "" } : {};
  return <input {...props} {...shown} className="w-full border-2 border-slate-300 rounded-lg p-3 h-12 text-[14px] focus:border-sky-500 outline-none bg-white" />;
}

function Pills({ options, value, onChange, testid, size = "min-h-[48px] px-3 text-[13px]" }) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map(([v, l, k]) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${k ?? v}`}
          className={`${size} rounded-lg border-2 font-medium ${value === v ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
          {value === v && testid === "walkin-pay" && <Check size={14} strokeWidth={2} className="inline mr-1 -mt-0.5" />}{l}
        </button>))}
    </div>
  );
}

function GuestSection({ form, update }) {
  const idTypes = useIdTypes();
  return (
    <div className="space-y-4" data-testid="walkin-step-0">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Salutation"><Pills options={["Mr", "Mrs", "Ms", "Dr"].map(s => [s, s])} value={form.salutation} onChange={v => update({ salutation: v })} testid="walkin-salutation" /></Field>
        <Field label="Govt ID type"><Pills options={idTypes.map(t => [t.value, t.label])} value={form.govt_id_type} onChange={v => update({ govt_id_type: v })} testid="walkin-id-type" size="min-h-[48px] px-2 text-[12px]" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name *"><TextInput value={form.first_name} onChange={e => update({ first_name: e.target.value })} data-testid="walkin-first-name" /></Field>
        <Field label="Last name"><TextInput value={form.last_name} onChange={e => update({ last_name: e.target.value })} data-testid="walkin-last-name" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Phone *"><TextInput value={form.primary_phone} onChange={e => update({ primary_phone: e.target.value })} data-testid="walkin-phone" /></Field>
        <Field label="Email"><TextInput type="email" value={form.email} onChange={e => update({ email: e.target.value })} data-testid="walkin-email" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Govt ID number"><TextInput value={form.govt_id_number} onChange={e => update({ govt_id_number: e.target.value })} data-testid="walkin-idnum" /></Field>
        <Field label="Nationality"><TextInput value={form.nationality || ""} onChange={e => update({ nationality: e.target.value })} placeholder="e.g. Indian, British, US" data-testid="walkin-nationality" /></Field>
      </div>
      <Field label="Govt ID document">
        <GovtIdUploader value={form.govt_id_document_url} onChange={(url) => update({ govt_id_document_url: url })} testid="walkin-idupload" />
      </Field>
    </div>
  );
}

function StaySection({ form, update, nights, rooms, loadingRooms, multi, roomCount, multiRoom }) {
  return (
    <div className="space-y-4" data-testid="walkin-step-1">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Check-in date *"><DatePicker value={form.expected_arrival} onChange={v => update({ expected_arrival: v, ...(nightsBetween(v, form.expected_departure) < 1 ? { expected_departure: addDays(v, 1) } : {}) })} data-testid="walkin-arrival" /></Field>
        <Field label="Check-out date *"><DatePicker value={form.expected_departure} onChange={v => update({ expected_departure: v })} minDate={form.expected_arrival} data-testid="walkin-departure" /></Field>
        <Field label="Check-in time"><TextInput type="time" value={form.arrival_time} onChange={e => update({ arrival_time: e.target.value })} data-testid="walkin-arrival-time" /></Field>
        <Field label="Check-out time"><TextInput type="time" value={form.departure_time} onChange={e => update({ departure_time: e.target.value })} data-testid="walkin-departure-time" /></Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Nights (auto, editable)">
          <TextInput type="number" min={1} value={nights || ""} onChange={e => { const n = parseInt(e.target.value) || 1; update({ expected_departure: addDays(form.expected_arrival, Math.max(n, 1)) }); }} data-testid="walkin-nights" />
        </Field>
        <Field label="Adults *"><TextInput type="number" min={1} value={form.num_adults} onChange={e => update({ num_adults: parseInt(e.target.value) || 1 })} data-testid="walkin-adults" /></Field>
        <Field label="Children"><TextInput type="number" min={0} value={form.num_children} onChange={e => update({ num_children: parseInt(e.target.value) || 0 })} data-testid="walkin-children" /></Field>
      </div>
      <ReservationSourceInput tone="light" testidPrefix="walkin-source" value={{ reservation_source: form.reservation_source, reservation_source_ref: form.reservation_source_ref }} onChange={(v) => update(v)} />
      <div className="flex items-center justify-between rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="walkin-breakfast-included">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Breakfast Included?</div>
          <div className="text-[11px] text-slate-500 mt-0.5">When Yes, breakfast is added for every night of the stay in the F&amp;B Breakfast Register.</div>
        </div>
        <Pills options={[[true, "Yes", "yes"], [false, "No", "no"]]} value={form.breakfast_included} onChange={v => update({ breakfast_included: v })} testid="walkin-breakfast" size="h-8 px-3 text-[13px]" />
      </div>
      <div>
        <div className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Number of Rooms</div>
        {roomCount}
      </div>
      {multi ? multiRoom : (
      <div>
        <div className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Select Room *</div>
        <FloorRoomPicker rooms={rooms} loading={loadingRooms} selectedRoomId={form.room?.id} onSelect={r => update({ room: r })}
          emptyLabel="No rooms configured for this hotel yet. Add rooms in Room Inventory to enable walk-ins." testid="walkin-room-picker" />
      </div>)}
    </div>
  );
}

function RequestsSection({ form, update }) {
  return (
    <div className="space-y-3">
      <Field label="Notes / preferences">
        <textarea value={form.special_requests} onChange={e => update({ special_requests: e.target.value })}
          className="w-full border-2 border-slate-300 rounded-lg p-2 text-[14px] min-h-[60px] bg-white" data-testid="walkin-notes" />
      </Field>
      <div className="flex flex-wrap gap-4">
        {[["early_checkin", "Early check-in"], ["late_checkout", "Late check-out"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
            <input type="checkbox" checked={!!form[k]} onChange={e => update({ [k]: e.target.checked })} className="w-4 h-4 accent-slate-900" data-testid={`walkin-${k.replace("_", "-")}`} />{l}
          </label>))}
      </div>
    </div>
  );
}

function PaymentSection({ form, update, totalPayable, advance }) {
  const full = totalPayable > 0 && Math.abs(advance - totalPayable) < 0.01;
  return (
    <div className="space-y-3">
      <Field label="Payment method"><Pills options={PAY_METHODS} value={form.pay_method} onChange={v => update({ pay_method: v })} testid="walkin-pay" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Reference no."><TextInput value={form.pay_reference} onChange={e => update({ pay_reference: e.target.value })} placeholder="UTR / TXN / card last 4" data-testid="walkin-pay-reference" /></Field>
        <Field label="Advance amount">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-[13px]">₹</span>
            <NumberInput min={0} step="0.01" value={form.advance_amount} onChange={e => update({ advance_amount: e.target.value })}
              className="w-full border-2 border-slate-300 rounded-lg p-3 pl-7 h-12 text-[14px] mono focus:border-sky-500 outline-none bg-white" data-testid="walkin-advance" />
          </div>
          {totalPayable > 0 && !full && (
            <button type="button" onClick={() => update({ advance_amount: String(totalPayable) })} className="mt-1 text-[11px] text-sky-700 underline" data-testid="walkin-advance-full">Collect full {fmtINR(totalPayable)}</button>)}
        </Field>
      </div>
      <div className="text-[11px] text-slate-500" data-testid="walkin-pay-note">
        {advance <= 0 ? "No advance — check in without payment; collect at checkout." : full ? "Full payment collected at check-in — a Room Payment Receipt (RCP) will be printed." : `Advance of ${fmtINR(advance)} — receipt printed; balance ${fmtINR(Math.max(totalPayable - advance, 0))} due at checkout.`}
      </div>
    </div>
  );
}
